<div class="page-container mi-ficha-page">
  <div class="page-header mb-4">
    <h1 class="page-title text-xl font-bold text-slate-900 dark:text-white">Mi Ficha</h1>
    <p class="page-subtitle text-xs sm:text-sm text-slate-500 dark:text-slate-400">
      Tipo de partner, condiciones de tu acuerdo y estado de tu contrato con SDi.
    </p>
  </div>

  @if (isLoading()) {
    <div class="loading-state flex flex-col items-center justify-center py-24">
      <div class="loading-spinner"></div>
      <p class="mt-4 text-slate-500 dark:text-slate-400 font-medium">Cargando ficha...</p>
    </div>
  } @else if (!partner) {
    <div class="detail-card text-center py-16">
      <fa-icon [icon]="faBuilding" class="text-3xl text-slate-200 dark:text-slate-700 mb-3"></fa-icon>
      <p class="text-sm text-slate-400 font-medium">No tienes una ficha de partner asociada.</p>
    </div>
  } @else {
    <!-- Empresa y tipo -->
    <div class="flex items-center gap-3 flex-wrap mb-6">
      <div class="partner-avatar">{{ partner.company_name.charAt(0).toUpperCase() }}</div>
      <div>
        <div class="flex items-center gap-2 flex-wrap">
          <h2 class="text-lg font-bold text-slate-900 dark:text-white">
            {{ partner.company_name }}
          </h2>
          @if (partner.type) {
            <span class="partner-type-chip">
              <fa-icon [icon]="faTags" class="mr-1"></fa-icon>
              {{ partner.type }}
            </span>
          }
        </div>
        <p class="text-xs text-slate-400 mt-0.5">
          Partner desde el {{ formatDate(partner.company_createdate) }}
        </p>
      </div>
    </div>

    <div class="flex flex-col lg:grid lg:grid-cols-3 lg:items-start gap-6">
      <!-- Main column -->
      <div class="order-2 lg:order-none lg:col-start-1 lg:col-span-2 space-y-6">
        <!-- Condiciones Generales -->
        <div class="detail-card">
          <h3 class="card-title">
            <fa-icon [icon]="faHandshake" class="mr-1.5 text-brand-red/70"></fa-icon>
            Condiciones Generales
          </h3>

          <div class="info-row mb-4">
            <div class="info-icon"><fa-icon [icon]="faHandshake"></fa-icon></div>
            <div class="info-data">
              <span class="label">Modelo de Relación</span>
              <span class="value">{{ partner.relationship_type || 'No definido' }}</span>
              @if (
                partner.relationship_type &&
                relationshipTypeDescriptions[partner.relationship_type]
              ) {
                <span class="hint">{{
                  relationshipTypeDescriptions[partner.relationship_type]
                }}</span>
              }
            </div>
          </div>

          <div class="section-divider"></div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faGaugeHigh"></fa-icon></div>
              <div class="info-data">
                <span class="label">Límite por Operación</span>
                <span class="value"
                  >{{
                    partner.operation_limit !== null && partner.operation_limit !== undefined
                      ? formatCurrency(partner.operation_limit)
                      : '-'
                  }}
                  @if (partner.operation_limit !== null && partner.operation_limit !== undefined) {
                    <span class="hint !mt-0 !inline">(Sin IVA)</span>
                  }
                </span>
              </div>
            </div>
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faCalendarDays"></fa-icon></div>
              <div class="info-data">
                <span class="label">Periodicidad Liquidación</span>
                <span class="value">{{ partner.settlement_periodicity || '-' }}</span>
              </div>
            </div>
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faUserPlus"></fa-icon></div>
              <div class="info-data">
                <span class="label">Mínimo Nuevos Clientes / Año</span>
                <span class="value">{{ partner.min_new_clients_per_year ?? '-' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Condiciones Comerciales -->
        <div class="detail-card">
          <h3 class="card-title">
            <fa-icon [icon]="faPercent" class="mr-1.5 text-brand-red/70"></fa-icon>
            Condiciones Comerciales
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faPercent"></fa-icon></div>
              <div class="info-data">
                <span class="label">Comisión Fija</span>
                <span class="value"
                  >{{
                    partner.fixed_commission !== null && partner.fixed_commission !== undefined
                      ? partner.fixed_commission + '%'
                      : 'Por producto'
                  }}</span
                >
                <span class="hint">Sobre proyectos e implantaciones.</span>
              </div>
            </div>
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faArrowsRotate"></fa-icon></div>
              <div class="info-data">
                <span class="label">Comisión Recurrente</span>
                <span class="value">{{ partner.recurring_commission ?? 0 }}%</span>
                <span class="hint">Sobre servicios recurrentes según condiciones vigentes.</span>
              </div>
            </div>
          </div>

          @if (partner.relationship_type === 'Distribuidor' && distributionModality) {
            <div class="section-divider"></div>
            <div class="distribution-group">
              <span class="label">Modelo de Distribución</span>
              <span class="value block">{{ distributionModality }}</span>
              @if (distributionModalityDescriptions[distributionModality]) {
                <p class="hint mt-1">
                  {{ distributionModalityDescriptions[distributionModality] }}
                </p>
              }
              @if (distributionModalityConditions[distributionModality]; as cond) {
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                  <div class="mini-stat">
                    <span class="label">Contratación</span>
                    <span class="value !text-sm">{{ cond.contracting }}</span>
                  </div>
                  <div class="mini-stat">
                    <span class="label">Implantación</span>
                    <span class="value !text-sm">{{ cond.implementation }}</span>
                  </div>
                  <div class="mini-stat">
                    <span class="label">Soporte</span>
                    <span class="value !text-sm">{{ cond.support }}</span>
                  </div>
                </div>
              }
            </div>
          }
        </div>

        <!-- Datos de la Empresa -->
        <div class="detail-card">
          <h3 class="card-title">
            <fa-icon [icon]="faBuilding" class="mr-1.5 text-brand-red/70"></fa-icon>
            Datos de la Empresa
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faEnvelope"></fa-icon></div>
              <div class="info-data">
                <span class="label">Email Corporativo</span>
                <span class="value">{{ partner.company_email || 'No disponible' }}</span>
              </div>
            </div>
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faPhone"></fa-icon></div>
              <div class="info-data">
                <span class="label">Teléfono</span>
                <span class="value">{{ partner.company_phone || 'No disponible' }}</span>
              </div>
            </div>
            <div class="info-row sm:col-span-2">
              <div class="info-icon"><fa-icon [icon]="faMapMarkerAlt"></fa-icon></div>
              <div class="info-data">
                <span class="label">Dirección Fiscal</span>
                <span class="value">{{ partner.company_address || 'No disponible' }}</span>
              </div>
            </div>
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faIdCard"></fa-icon></div>
              <div class="info-data">
                <span class="label">CIF</span>
                <span class="value font-mono">{{ partner.cif || 'No definido' }}</span>
              </div>
            </div>
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faCreditCard"></fa-icon></div>
              <div class="info-data">
                <span class="label">IBAN de Empresa</span>
                <span class="value font-mono">{{ partner.company_iban || 'No definido' }}</span>
              </div>
            </div>
            <div class="info-row">
              <div class="info-icon"><fa-icon [icon]="faHandshake"></fa-icon></div>
              <div class="info-data">
                <span class="label">Contacto SDi</span>
                <span class="value">{{ sdiContactName }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Documentos -->
        <div class="detail-card">
          <h3 class="card-title">
            <fa-icon [icon]="faFile" class="mr-1.5 text-brand-red/70"></fa-icon>
            Mis Documentos
          </h3>

          <div class="space-y-3">
            @if (isFilesLoading()) {
              <div class="flex flex-col items-center py-6 opacity-50">
                <div class="loading-spinner"></div>
                <span class="text-[10px] mt-2">Cargando...</span>
              </div>
            } @else if (files.length === 0) {
              <div class="empty-box">
                <fa-icon [icon]="faFile" class="text-2xl text-slate-200 dark:text-slate-700 mb-2"></fa-icon>
                <p class="text-[10px] text-slate-400 font-medium">
                  No hay documentos disponibles para descargar.
                </p>
              </div>
            } @else {
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                @for (file of files; track file.id) {
                  <div class="file-row">
                    <div class="file-row__icon"><fa-icon [icon]="faFile"></fa-icon></div>
                    <div class="flex-1 min-w-0">
                      <p class="value truncate" [title]="file.name">{{ file.name }}</p>
                      <span class="text-[10px] text-slate-400">{{ formatBytes(file.size) }}</span>
                    </div>
                    <button
                      type="button"
                      (click)="downloadFile(file)"
                      class="icon-btn"
                      title="Descargar"
                    >
                      <fa-icon [icon]="faDownload"></fa-icon>
                    </button>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <!--
        Sidebar: en móvil se "desenvuelve" (display:contents) para que Mi Contrato y
        Actividad Reciente puedan intercalarse alrededor de la columna principal
        mediante `order`; en escritorio vuelve a ser una columna normal e independiente
        en altura de la columna principal.
      -->
      <div class="contents lg:flex lg:flex-col lg:gap-6 lg:col-start-3">
        <!-- Contrato: primero en móvil (lo más importante) -->
        <div
          class="order-1 lg:order-none detail-card"
          [class.detail-card--pending]="contract && contract.status !== 'signed'"
        >
          <h3 class="card-title">
            <fa-icon [icon]="faFileContract" class="mr-1.5 text-brand-red/70"></fa-icon>
            Mi Contrato
          </h3>

          @if (contract) {
            <div class="space-y-3">
              <div class="info-row">
                <div class="info-icon"><fa-icon [icon]="faFileContract"></fa-icon></div>
                <div class="info-data">
                  <span class="label">Nº de Contrato</span>
                  <span class="value">{{ contract.number }}</span>
                </div>
              </div>

              <div class="flex items-center gap-2">
                <span
                  class="status-badge"
                  [class.status-badge--signed]="contract.status === 'signed'"
                  [class.status-badge--pending]="contract.status !== 'signed'"
                >
                  {{ contract.status === 'signed' ? 'Firmado' : 'Pendiente de firma' }}
                </span>
              </div>

              @if (contract.status === 'signed') {
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faCalendarDays"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Fecha de Firma</span>
                    <span class="value">{{ formatDate(contract.signed_at) }}</span>
                  </div>
                </div>
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faClockRotateLeft"></fa-icon></div>
                  <div class="info-data">
                    <span class="label">Fecha de Renovación</span>
                    <span class="value">{{ formatDate(contract.renewal_date) }}</span>
                  </div>
                </div>
              } @else {
                <p class="text-[11px] text-amber-600 dark:text-amber-400 font-medium leading-relaxed">
                  Este contrato está pendiente de firma. Fírmalo para formalizar tu acuerdo con
                  SDi.
                </p>
              }

              @if (contract.document_name) {
                <div class="info-row">
                  <div class="info-icon"><fa-icon [icon]="faFile"></fa-icon></div>
                  <div class="info-data flex-1 min-w-0">
                    <span class="label">Documento</span>
                    <div class="flex items-center gap-2">
                      <span class="value truncate" [title]="contract.document_name">{{
                        contract.document_name
                      }}</span>
                      <button
                        type="button"
                        (click)="downloadContract(contract.number, contract.document_name)"
                        class="icon-btn"
                        title="Descargar contrato"
                      >
                        <fa-icon [icon]="faDownload"></fa-icon>
                      </button>
                    </div>
                  </div>
                </div>
              }

              @if (contract.status !== 'signed') {
                <button
                  type="button"
                  class="app-btn app-btn--primary w-full justify-center mt-1"
                  (click)="openSignModal()"
                >
                  <fa-icon [icon]="faPenNib"></fa-icon>
                  Firmar contrato
                </button>
              }
            </div>
          } @else {
            <p class="text-xs text-slate-400">Aún no tienes un contrato registrado.</p>
          }
        </div>

        <!-- Actividad Reciente: al final en móvil -->
        <div class="order-3 lg:order-none detail-card">
          <h3 class="card-title">
            <fa-icon [icon]="faClockRotateLeft" class="mr-1.5 text-brand-red/70"></fa-icon>
            Actividad Reciente
          </h3>

          @if (isConditionLogsLoading && conditionLogs.length === 0) {
            <div class="flex flex-col items-center justify-center py-8 gap-3">
              <div class="loading-spinner"></div>
              <p class="text-slate-400 text-[10px]">Cargando actividad...</p>
            </div>
          } @else if (conditionLogsError) {
            <div class="empty-box">
              <p class="text-[10px] text-slate-400 font-medium mb-2">
                No se pudo cargar la actividad.
              </p>
              <button
                type="button"
                (click)="loadConditionLogs(partner.id.toString())"
                class="text-[10px] font-semibold text-brand-red hover:underline"
              >
                Reintentar
              </button>
            </div>
          } @else if (conditionLogs.length === 0) {
            <div class="empty-box">
              <fa-icon [icon]="faClock" class="text-2xl text-slate-200 dark:text-slate-700 mb-2"></fa-icon>
              <p class="text-[10px] text-slate-400 font-medium">Sin actividad reciente.</p>
            </div>
          } @else {
            <div class="activity-timeline-scroll">
              <div
                class="space-y-4 relative before:absolute before:inset-0 before:ml-3.5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent dark:before:via-slate-800"
              >
                @for (log of conditionLogs; track log.id) {
                  <div class="relative flex items-start gap-3">
                    <div
                      class="flex-shrink-0 w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 flex items-center justify-center z-10 text-[10px]"
                    >
                      <fa-icon [icon]="faPen"></fa-icon>
                    </div>
                    <div
                      class="flex-1 min-w-0 bg-white dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 p-3 rounded-xl"
                    >
                      <div class="flex items-center justify-between gap-2 mb-1.5">
                        <span
                          class="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                        >
                          {{ conditionLogFieldLabel(log.field) }}
                        </span>
                        <time class="text-[9px] text-slate-400 flex items-center gap-1 whitespace-nowrap">
                          <fa-icon [icon]="faClock"></fa-icon>
                          {{ log.created_at | date: 'dd/MM/yyyy HH:mm' }}
                        </time>
                      </div>
                      <p class="text-xs text-slate-700 dark:text-slate-300">
                        <span class="line-through text-slate-400">{{ log.old_value ?? '-' }}</span>
                        <fa-icon [icon]="faArrowRight" class="text-[9px] mx-1.5 text-slate-300"></fa-icon>
                        <span class="font-semibold text-slate-900 dark:text-white">{{
                          log.new_value ?? '-'
                        }}</span>
                      </p>
                      <p class="text-[9px] text-slate-400 mt-1.5">
                        @if (log.source === 'automatic' || !log.user) {
                          Cambio automático por modelo
                        } @else {
                          Por {{ log.user.name }} ({{ log.user.email }})
                        }
                      </p>
                    </div>
                  </div>
                }
              </div>
            </div>

            @if (conditionLogsPage < conditionLogsLastPage) {
              <div class="text-center mt-4">
                <button
                  type="button"
                  (click)="loadMoreConditionLogs()"
                  [disabled]="isConditionLogsLoading"
                  class="text-[10px] font-semibold text-brand-red hover:underline disabled:opacity-50"
                >
                  {{ isConditionLogsLoading ? 'Cargando...' : 'Ver más' }}
                </button>
              </div>
            }
          }
        </div>
      </div>
    </div>
  }
</div>

@if (showSignModal() && contract) {
  <app-signature-pad-modal
    [contractNumber]="contract.number"
    [documentName]="contract.document_name"
    (close)="closeSignModal()"
    (signed)="onContractSigned()"
  ></app-signature-pad-modal>
}
